import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterDTOType, WorkflowCreateDTOType } from '../../schemas';

type WorkflowFormType = WorkflowCreateDTOType | (WorkflowCreateDTOType & { id: number });

interface ITasksState {
  isDrawerOpen: boolean;
  workflowCreatePayload: WorkflowFormType | null;
  filters: Array<FilterDTOType>;
  promptTemplateFilters: Array<FilterDTOType>;
}

const initialState: ITasksState = {
  isDrawerOpen: false,
  workflowCreatePayload: null,
  filters: [],
  promptTemplateFilters: [],
};

const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {
    setIsDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isDrawerOpen = action.payload;
    },
    updateWorkflowCreatePayload: (state, action: PayloadAction<Partial<WorkflowFormType>>) => {
      if (state.workflowCreatePayload) {
        state.workflowCreatePayload = {
          ...state.workflowCreatePayload,
          ...action.payload,
        };
      } else {
        state.workflowCreatePayload = action.payload as WorkflowFormType;
      }

      if (!state.workflowCreatePayload.scheduleType) {
        state.workflowCreatePayload.scheduleType = 'ONGOING';
      }
    },
    clearWorkflowCreatePayload: state => {
      state.workflowCreatePayload = null;
    },
    setWorkflowFilters: (state, action: PayloadAction<Array<FilterDTOType>>) => {
      state.filters = action.payload;
    },
    clearWorkflowFilters: state => {
      state.filters = [];
    },
    addPromptTemplateFilter: (state, action: PayloadAction<FilterDTOType>) => {
      const existingIndex = state.promptTemplateFilters.findIndex(
        filter =>
          filter.field === action.payload.field && filter.filterType === action.payload.filterType,
      );

      if (existingIndex !== -1) {
        state.promptTemplateFilters[existingIndex] = action.payload;
      } else {
        state.promptTemplateFilters.push(action.payload);
      }
    },
    removePromptTemplateFilter: (
      state,
      action: PayloadAction<{ field: string; filterType: string }>,
    ) => {
      state.promptTemplateFilters = state.promptTemplateFilters.filter(
        filter =>
          !(
            filter.field === action.payload.field && filter.filterType === action.payload.filterType
          ),
      );
    },
    clearAllPromptTemplateFilters: state => {
      state.promptTemplateFilters = [];
    },
  },
});

export const {
  setIsDrawerOpen,
  clearWorkflowCreatePayload,
  updateWorkflowCreatePayload,
  setWorkflowFilters,
  addPromptTemplateFilter,
  removePromptTemplateFilter,
  clearAllPromptTemplateFilters,
} = workflowsSlice.actions;

export const workflowsReducer = workflowsSlice.reducer;

export const selectWorkflowFilters = (state: { workflows: ITasksState }) => state.workflows.filters;

export const selectPromptTemplateFilters = (state: { workflows: ITasksState }) =>
  state.workflows.promptTemplateFilters;
